import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import ResetPassword from "@/pages/ResetPassword";
import ProfileSetup from "@/pages/ProfileSetup";
import Home from "@/pages/Home";
import PlayHub from "@/pages/PlayHub";
import KnowledgeCoverage from "@/pages/KnowledgeCoverage";
import RanksHub from "@/pages/RanksHub";
import Duel from "@/pages/Duel";
import Daily from "@/pages/Daily";
import Ladder from "@/pages/Ladder";
import Survival from "@/pages/Survival";
import Leaderboard from "@/pages/Leaderboard";
import CountryPage from "@/pages/CountryPage";
import Profile from "@/pages/Profile";
import HallOfFame from "@/pages/HallOfFame";
import ChampionshipLive from "@/pages/ChampionshipLive";
import Saturday from "@/pages/Saturday";
import Chat from "@/pages/Chat";
import History from "@/pages/History";
import SignalsPage from "@/pages/Signals";
import SignalDetail from "@/pages/SignalDetail";
import Scoring from "@/pages/Scoring";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminPlayers from "@/pages/admin/Players";
import AdminPlayerProfile from "@/pages/admin/PlayerProfile";
import AdminQuestions from "@/pages/admin/Questions";
import AdminNKE from "@/pages/admin/NKE";
import AdminNKDaily from "@/pages/admin/NKDaily";
import AdminNKLadder from "@/pages/admin/NKLadder";
import AdminNKDuel from "@/pages/admin/NKDuel";
import AdminNKSurvival from "@/pages/admin/NKSurvival";
import AdminSaturday from "@/pages/admin/SaturdayAdmin";
import AdminSeasons from "@/pages/admin/Seasons";
import AdminChampionshipOps from "@/pages/admin/ChampionshipOps";
import AdminCountryWar from "@/pages/admin/CountryWarAdmin";
import AdminRewards from "@/pages/admin/Rewards";
import AdminSponsors from "@/pages/admin/Sponsors";
import AdminModeration from "@/pages/admin/Moderation";
import AdminReports from "@/pages/admin/ReportsAdmin";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminSettings from "@/pages/admin/Settings";
import AdminSubAdmins from "@/pages/admin/SubAdmins";
import AdminModesStatus from "@/pages/admin/ModesStatus";
import AdminAds from "@/pages/admin/Ads";
import AdminHeatmap from "@/pages/admin/Heatmap";
import AdminPartnerInquiries from "@/pages/admin/PartnerInquiries";
import AdminFeedback from "@/pages/admin/Feedback";
import AdminUsedBank from "@/pages/admin/UsedBank";
import AdminChampionshipBank from "@/pages/admin/ChampionshipBank";
import AdminCountries from "@/pages/admin/Countries";
import AdminCountryAnalytics from "@/pages/admin/CountryAnalytics";
import AdminExpansionWatchlist from "@/pages/admin/ExpansionWatchlist";
import AdminAmbassadors from "@/pages/admin/Ambassadors";
import AdminVerifications from "@/pages/admin/Verifications";
import AdminSignals from "@/pages/admin/Signals";
import Partner from "@/pages/Partner";
import PublicProfile from "@/pages/PublicProfile";
import EmbedCard from "@/pages/EmbedCard";
import AppShell from "@/components/AppShell";

// NKognito Live — invite-only hosted live-event platform
import { HostAuthProvider } from "@/contexts/HostAuthContext";
import HostSetup from "@/pages/host/HostSetup";
import HostLogin from "@/pages/host/HostLogin";
import HostConsole from "@/pages/host/HostConsole";
import HostEventControl from "@/pages/host/HostEventControl";
import PublicCanvas from "@/pages/live/PublicCanvas";
import PlayerView from "@/pages/live/PlayerView";
import AdminLiveHosts from "@/pages/admin/LiveHosts";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const fragment = window.location.hash;
    const params = new URLSearchParams(fragment.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (data.session_token) localStorage.setItem("nk_session_token", data.session_token);
        setUser(data.user);
        window.history.replaceState(null, "", "/");
        if (!data.user?.country || !data.user?.username) {
          navigate("/profile-setup", { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      } catch {
        toast.error("Authentication failed");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-nk-bg text-nk-text">
      <div className="font-mono text-nk-accent">Signing you in…</div>
    </div>
  );
}

function Protected({ children, requireProfile = true, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nk-bg">
        <div className="font-mono text-nk-mute text-sm">Connecting…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  const isAdmin = user.role === "super_admin" || user.role === "sub_admin" || user.is_admin;
  if (adminOnly && !isAdmin) return <Navigate to="/home" replace />;
  // Admins skip the country+username profile-setup gate (they manage, they don't play).
  if (requireProfile && !isAdmin && (!user.country || !user.username)) {
    return <Navigate to="/profile-setup" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Visit tracking — fire once per browser session. localStorage holds the
  // visitor_id forever so we can distinguish new vs returning visitors.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("nk_visit_tracked") === "1") return;
      let vid = localStorage.getItem("nk_visitor_id");
      if (!vid) {
        vid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        localStorage.setItem("nk_visitor_id", vid);
      }
      sessionStorage.setItem("nk_visit_tracked", "1");
      const referrer = (document.referrer || "").slice(0, 400) || null;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visits/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: vid, referrer }),
        credentials: "omit",
        keepalive: true,
      }).catch(() => {});
    } catch {/* localStorage disabled — silently skip */}
  }, []);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/partner" element={<Partner />} />
      <Route path="/country/:code" element={<CountryPage />} />
      <Route path="/u/:handle" element={<PublicProfile />} />
      <Route path="/embed/card/:handle" element={<EmbedCard />} />
      <Route path="/championship/live" element={<ChampionshipLive />} />
      {/* NKognito Live — separate host surface (no AppShell, own auth context) */}
      <Route path="/host/setup/:setupToken" element={<HostAuthProvider><HostSetup /></HostAuthProvider>} />
      <Route path="/host/login" element={<HostAuthProvider><HostLogin /></HostAuthProvider>} />
      <Route path="/host/console" element={<HostAuthProvider><HostConsole /></HostAuthProvider>} />
      <Route path="/live/:code/host" element={<HostAuthProvider><HostEventControl /></HostAuthProvider>} />
      <Route path="/live/:code/screen" element={<PublicCanvas />} />
      <Route path="/live/:code/play" element={<PlayerView />} />
      <Route path="/profile-setup" element={<Protected requireProfile={false}><ProfileSetup /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/home" element={<Home />} />
        <Route path="/play" element={<PlayHub />} />
        <Route path="/coverage" element={<KnowledgeCoverage />} />
        <Route path="/ranks" element={<RanksHub />} />
        <Route path="/lobby" element={<Navigate to="/chat" replace />} />
        <Route path="/daily" element={<Daily />} />
        <Route path="/ladder" element={<Ladder />} />
        <Route path="/duel" element={<Duel />} />
        <Route path="/survival" element={<Survival />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/hall-of-fame" element={<HallOfFame />} />
        <Route path="/championship" element={<Navigate to="/championship/live" replace />} />
        <Route path="/saturday" element={<Saturday />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/history" element={<History />} />
        <Route path="/signals" element={<SignalsPage />} />
        <Route path="/signals/:sid" element={<SignalDetail />} />
        <Route path="/scoring" element={<Scoring />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/:user_id" element={<Profile />} />
      </Route>
      <Route path="/admin" element={<Protected adminOnly={true}><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="players" element={<AdminPlayers />} />
        <Route path="players/:userId" element={<AdminPlayerProfile />} />
        <Route path="questions" element={<AdminQuestions />} />
        <Route path="nke" element={<AdminNKE />} />
        <Route path="daily" element={<AdminNKDaily />} />
        <Route path="ladder" element={<AdminNKLadder />} />
        <Route path="duel" element={<AdminNKDuel />} />
        <Route path="survival" element={<AdminNKSurvival />} />
        <Route path="saturday" element={<AdminSaturday />} />
        <Route path="seasons" element={<AdminSeasons />} />
        <Route path="championships" element={<Navigate to="/admin/championship" replace />} />
        <Route path="championship" element={<AdminChampionshipOps />} />
        <Route path="country-war" element={<AdminCountryWar />} />
        <Route path="rewards" element={<AdminRewards />} />
        <Route path="sponsors" element={<AdminSponsors />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="admins" element={<AdminSubAdmins />} />
        <Route path="modes-status" element={<AdminModesStatus />} />
        <Route path="ads" element={<AdminAds />} />
        <Route path="heatmap" element={<AdminHeatmap />} />
        <Route path="partner-inquiries" element={<AdminPartnerInquiries />} />
        <Route path="feedback" element={<AdminFeedback />} />
        <Route path="used-bank" element={<AdminUsedBank />} />
        <Route path="championship-bank" element={<AdminChampionshipBank />} />
        <Route path="countries" element={<AdminCountries />} />
        <Route path="country-analytics" element={<AdminCountryAnalytics />} />
        <Route path="expansion-watchlist" element={<AdminExpansionWatchlist />} />
        <Route path="ambassadors" element={<AdminAmbassadors />} />
        <Route path="verifications" element={<AdminVerifications />} />
        <Route path="live-hosts" element={<AdminLiveHosts />} />
        <Route path="signals" element={<AdminSignals />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
